import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EmailIcon from "@mui/icons-material/Email";
import FmdGoodIcon from "@mui/icons-material/FmdGood";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Box, Button } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useFormik } from "formik";
import GoogleMapReact from "google-map-react";
import { useContext, useEffect, useMemo, useState } from "react";
import { string, object } from "yup";

import { Form } from "components/Form";
import { FormTextField } from "components/FormTextField";
import { Header } from "components/Header";
import { Paragraph } from "components/Paragraph";
import { ContactInfo } from "components/screens/contact/ContactInfo";
import { RrhhBanner } from "components/screens/contact/RrhhBanner";
import { Title } from "components/Title";
import { ContactContext } from "contexts/ContactProvider";
import { useLayout } from "hooks/useLayout";
import { useToast } from "hooks/useToast";
import { API } from "utils/api";
import {
  BLUECAR_ADDRESS_HUMAN,
  BLUECAR_EMAIL,
  BLUECAR_GMAPS,
  BLUECAR_PHONE,
  BLUECAR_PHONE_HUMAN,
  BLUECAR_WHATSAPP,
  BLUECAR_WHATSAPP_HUMAN,
} from "utils/contact";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomMapComponent = (props: any) => (
  <FmdGoodIcon titleAccess={props.text} />
);

export const Contact = () => {
  const { PublicLayout } = useLayout({ title: "Contacto" });
  const { infoToast, errorToast } = useToast();
  const { message, setMessage } = useContext(ContactContext);
  const [focusNameField, setFocusNameField] = useState(false);

  const CrmScript = useMemo(
    () => (
      <script
        type="text/javascript"
        src="https://leads.godixital.com/js/leads_form_trigger.js"
        async
        defer
      />
    ),
    []
  );

  const { mutate: createContact, isLoading } = useMutation({
    mutationFn: (form: {
      name: string;
      company: string;
      email: string;
      phone: string;
      message: string;
    }) => API.post("/contacts/new_customer", form),
    onSuccess: () =>
      infoToast(
        "Se ha enviado tu contacto. Alguien se comunicará contigo pronto."
      ),
    onError: () =>
      errorToast("No se ha podido enviar tu contacto, intenta nuevamente"),
  });

  const formik = useFormik({
    initialValues: { name: "", company: "", email: "", phone: "", message: "" },
    validationSchema: object({
      name: string().required("Requerido"),
      company: string().required("Requerido"),
      email: string().email("Email inválido").required("Requerido"),
      phone: string().required("Requerido"),
      message: string().required("Requerido"),
    }),
    onSubmit: (values) =>
      createContact(values, {
        onSuccess: () => {
          formik.resetForm();
        },
      }),
  });

  useEffect(() => {
    if (message) {
      formik.setFieldValue("message", message);
      setMessage(undefined);
      setFocusNameField(true);
    }
  }, [message, setMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PublicLayout>
      <Header titleText="Contacto" />
      <Box paddingY={3} paddingX={{ xs: 3, md: 7 }}>
        <Box
          display="flex"
          sx={{ flexDirection: { xs: "column-reverse", md: "row" } }}
          justifyContent="space-between"
          paddingTop={2}
          paddingX={{ xs: 0, md: 10 }}
        >
          <Box
            display="flex"
            flexGrow={1}
            flexDirection="column"
            paddingX={{ xs: 0, md: 2 }}
            sx={{ marginBottom: 4, alignItems: { xs: "center", md: "normal" } }}
          >
            <Title font="xl" style={{ marginBottom: 2 }}>
              Nuestros datos
            </Title>
            <ContactInfo
              href={`tel:${BLUECAR_PHONE}`}
              Icon={PhoneIcon}
              text={BLUECAR_PHONE_HUMAN}
            />
            <ContactInfo
              href={`https://api.whatsapp.com/send/?phone=${BLUECAR_WHATSAPP}&text=%C2%A1Hola!`}
              Icon={WhatsAppIcon}
              text={BLUECAR_WHATSAPP_HUMAN}
            />
            <ContactInfo
              href={`mailto:${BLUECAR_EMAIL}`}
              Icon={EmailIcon}
              text={BLUECAR_EMAIL}
            />
            <ContactInfo
              Icon={CalendarMonthIcon}
              text="Lunes de a Viernes de 8:00 a 17:30 hs"
            />
            <ContactInfo
              href={BLUECAR_GMAPS}
              Icon={LocationOnIcon}
              text={BLUECAR_ADDRESS_HUMAN}
            />
            <Box height={200} width="100%">
              <GoogleMapReact
                defaultCenter={{
                  lat: -34.647529,
                  lng: -58.377027,
                }}
                defaultZoom={17}
              >
                <CustomMapComponent lat={-34.647529} lng={-58.377027} />
              </GoogleMapReact>
            </Box>
          </Box>
          <Box paddingX={{ xs: 0, md: 2 }} sx={{ marginBottom: 4 }}>
            <Title font="xl">Contáctate</Title>
            <Paragraph font="xl" style={{ marginTop: 1 }}>
              Si te quieres comunicar con nosotros completa este formulario y
              nos pondremos en contacto.
            </Paragraph>
            <Form formik={formik}>
              <FormTextField
                name="name"
                label="Nombre completo"
                type="text"
                focus={focusNameField}
                fullWidth
              />
              <FormTextField
                name="company"
                label="Empresa"
                type="text"
                fullWidth
              />
              <Box
                display="flex"
                flexDirection={{ xs: "column", md: "row" }}
                gap={{ xs: 0, md: 2 }}
              >
                <FormTextField
                  name="email"
                  label="Email"
                  type="email"
                  fullWidth
                />
                <FormTextField
                  name="phone"
                  label="Teléfono"
                  type="text"
                  fullWidth
                />
              </Box>
              <FormTextField
                name="message"
                label="Mensaje"
                placeholder="Cuéntanos la razón de tu contacto"
                type="text"
                minRows={5}
                multiline
              />
              <Button
                type="submit"
                variant="contained"
                style={{ marginTop: 8, marginBottom: 8 }}
                disabled={isLoading || !formik.isValid}
              >
                {isLoading ? "Enviando..." : "Enviar contacto"}
              </Button>
            </Form>
          </Box>
        </Box>
      </Box>
      <RrhhBanner />
      {CrmScript}
    </PublicLayout>
  );
};
